import {
  EnrollUserRequest,
  Partner,
  ShoppingCartRequest,
  CreateUserRequest,
} from 'packages/smart-home-support/api/models';
import {
  SecurityTokenResponse,
  SecurityTokenRequest,
  GetTransactionIdResponse,
  GetTaxDetailsResponse,
  AJAXConfig,
  Payment,
  TransactionContact,
  EnrollUserResponse,
} from './models';
import axios, { AxiosResponse } from 'axios';
import config from './config';
import {
  PaymentClient,
  Env,
  BillingContactInfo,
  CreditCardInfo,
  SessionData,
  PaymentResponse,
} from '@backoffice/fast-payments-client-js-sdk';
import { getPartner } from 'packages/smart-home-support/partner/utils';
import { mapBillingToBGv3 } from './mappers/billingToBGv3';
import { ShCustomerInfoStore } from '@soluto-private/sh-customer-info-store-api-client';
import { ApolloClient, InMemoryCache, gql, NormalizedCacheObject } from '@apollo/client';

export interface SmartHomeApiClient {
  getTaxes: (
    correlationId: string,
    city: string,
    state: string,
    zip: string,
    partner: Partner
  ) => Promise<GetTaxDetailsResponse>;
  enrollUser: (request: EnrollUserRequest) => Promise<EnrollUserResponse>;
  saveShoppingCart: (request: ShoppingCartRequest) => Promise<void>;
  getSecurityToken: (phoneNumber: string, correlationId: string, partner: Partner) => Promise<SecurityTokenResponse>;
  verifyTransactionById: (
    phoneNumber: string,
    correlationId: string,
    partner: Partner
  ) => Promise<GetTransactionIdResponse>;
  createCreditCardTransaction: (
    ajaxConfig: AJAXConfig,
    payment: Payment,
    transactionContact: TransactionContact,
    token: string
  ) => Promise<string>;
  createUserIfNotPresent: (request: CreateUserRequest) => Promise<void>;
  getUserDetails: (customerId: string, partner: string) => Promise<ShCustomerInfoStore>;
}

export const GET_CUSTOMER_INFO_FROM_STORE = gql`
  query getCustomerInfoFromStore($partner: String!, $id: String!) {
    getCustomerInfoFromStore(input: { partner: $partner, id: $id }) {
      id
      partner
      firstName
      lastName
      mdn
      email
      homeAddress
      billingAddress
    }
  }
`;

const getRestClient = (baseUrl: string) =>
  axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

const getApolloClient = (uri: string): ApolloClient<NormalizedCacheObject> => {
  const cache = new InMemoryCache();
  return new ApolloClient({
    uri,
    cache,
  });
};

const getUserPoolRestClient = () => getRestClient(config.SH_USER_POOL_API_BASE_URL);
const getEnrollmentRestClient = () => getRestClient(config.SH_ENROLLMENT_API_BASE_URL);
const getUserDetailsApolloClient = () => getApolloClient(config.SH_USER_DETAILS_API_BASE_URL);

const getSecurityTokenRequest = (
  phoneNumber: string,
  correlationId: string,
  partner: Partner,
  sourceApp?: string
): SecurityTokenRequest => {
  return {
    phoneNumber,
    partner,
    correlationId,
    sourceApp,
  };
};

function delayedResponse<T>(response: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(response), 3000));
}

async function getSecurityToken(
  phoneNumber: string,
  correlationId: string,
  partner: Partner
): Promise<SecurityTokenResponse> {
  const restClient = getEnrollmentRestClient();
  const sourceApp = getPartner().productCode;

  const request: SecurityTokenRequest = getSecurityTokenRequest(phoneNumber, correlationId, partner, sourceApp);

  if (config.IS_DEMO_MODE) {
    return await delayedResponse<SecurityTokenResponse>({ token: 'asf', encryptionKey: '234' });
  } else {
    const response: AxiosResponse<SecurityTokenResponse> = await restClient.post(`smart-home/billing/token`, request);

    return response.data;
  }
}

const verifyTransactionByIdQueryParams = (phoneNumber: string, correlationId: string, partner: Partner): string => {
  return [`correlationId=${correlationId}`, `phoneNumber=${phoneNumber}`, `partner=${partner}`].join('&');
};

async function verifyTransactionById(
  phoneNumber: string,
  correlationId: string,
  partner: Partner
): Promise<GetTransactionIdResponse> {
  const restClient = getEnrollmentRestClient();

  const requestQueryParams: string = verifyTransactionByIdQueryParams(phoneNumber, correlationId, partner);

  if (config.IS_DEMO_MODE) {
    return await delayedResponse<GetTransactionIdResponse>({ pciToken: '123', billingAccountNumber: '123' });
  } else {
    const response: AxiosResponse<GetTransactionIdResponse> = await restClient.get(
      `smart-home/billing/transaction?${requestQueryParams}`
    );

    return response.data;
  }
}

const getTaxDetailsQueryParams = (
  correlationId: string,
  city: string,
  state: string,
  zip: string,
  partner: Partner
): string => {
  return [
    `correlationId=${correlationId}`,
    `partner=${partner}`,
    `city=${city}`,
    `state=${state}`,
    `postalCode=${zip}`,
  ].join('&');
};

async function getTaxes(
  correlationId: string,
  city: string,
  state: string,
  zip: string,
  partner: Partner
): Promise<GetTaxDetailsResponse> {
  const restClient = getEnrollmentRestClient();

  const requestQueryParams = getTaxDetailsQueryParams(correlationId, city, state, zip, partner);

  if (config.IS_DEMO_MODE) {
    return await delayedResponse<GetTaxDetailsResponse>({ totalTaxAmount: '1.85' });
  } else {
    const response: AxiosResponse<GetTaxDetailsResponse> = await restClient.get(
      `smart-home/billing/tax?${requestQueryParams}`
    );

    return response.data;
  }
}

async function enrollUser(request: EnrollUserRequest): Promise<EnrollUserResponse> {
  const restClient = getEnrollmentRestClient();

  if (config.IS_DEMO_MODE) {
    return await delayedResponse<EnrollUserResponse>({ planId: '123843293' });
  } else {
    const response: AxiosResponse<EnrollUserResponse> = await restClient.post(`smart-home/enrollment`, request);
    return response.data;
  }
}

async function getUserDetails(communicationId: string, partner: string): Promise<ShCustomerInfoStore> {
  const apolloClient = getUserDetailsApolloClient();
  const query = GET_CUSTOMER_INFO_FROM_STORE;
  const variables = {
    id: communicationId,
    partner,
  };

  const customerInfoResult = await apolloClient.query({ query, variables });
  return customerInfoResult.data.getCustomerInfoFromStore;
}

async function saveShoppingCart(request: ShoppingCartRequest): Promise<void> {
  const restClient = getEnrollmentRestClient();
  await restClient.post(`smart-home/enrollment/shopping-cart`, request);
}

const createCreditCardTransaction = async (
  ajaxConfig: AJAXConfig,
  payment: Payment,
  transactionContact: TransactionContact,
  token: string
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const success = (data: string) => {
      const response = {};
      data
        .split(', ')
        .map((str) => str.split(': '))
        .forEach((prop) => {
          response[prop[0]] = prop[1];
        });
      if (response['rtn_code'] === 'BG-0') {
        resolve('success');
      } else {
        reject(`Error creating transaction: ${data}`);
      }
    };

    const failure = (error) => {
      reject('Failed to create transaction');
    };

    if (config.IS_DEMO_MODE) {
      setTimeout(() => resolve(), 3000);
    } else {
      postPayment(ajaxConfig, payment, transactionContact, token, success, failure);
    }
  });
};

async function postPayment(
  ajaxConfig: AJAXConfig,
  payment: Payment,
  transactionContact: TransactionContact,
  sessionId: string,
  success: (data: any) => void,
  failure: (error) => void
): Promise<void> {
  const { appName, encryption_key } = ajaxConfig;

  // Set up data
  const sessionData: SessionData = {
    // For backwards-compatibility with legacy systems
    appName,
    encryptionKey: encryption_key, // From getSecurityToken call
    currency: 'USD',
  };

  const billingContact: BillingContactInfo = mapBillingToBGv3(transactionContact);

  // For Credit Card Payments
  const creditCardInfo: CreditCardInfo = {
    number: `${payment.cc_no}`,
    securityCode: `${payment.cvv}`,
    expiration: { month: `${payment.cc_exp_month}`, year: `${payment.cc_exp_year}` },
  };

  // Process payment
  try {
    const paymentClient: PaymentClient = new PaymentClient({ env: config.FAST_PAYMENT_ENV });
    await paymentClient.addSession(sessionId, sessionData);
    await paymentClient.addBillingContactInfo(billingContact);
    await paymentClient.addCreditCardInfo(creditCardInfo);
    const response: PaymentResponse = await paymentClient.processPayment();
    success('rtn_code: BG-0');
  } catch (e) {
    failure(e);
    // Can throw PaymentClientError (or its subclass, PaymentValidationError)
  }
}

async function createUserIfNotPresent(request: CreateUserRequest): Promise<void> {
  const restClient = getUserPoolRestClient();

  if (config.IS_DEMO_MODE) {
    return await delayedResponse<void>(undefined);
  } else {
    const response: AxiosResponse<void> = await restClient.post(`user`, request);
    return response.data;
  }
}

const smartHomeApiClient: SmartHomeApiClient = {
  getTaxes,
  enrollUser,
  saveShoppingCart,
  getSecurityToken,
  verifyTransactionById,
  createCreditCardTransaction,
  createUserIfNotPresent,
  getUserDetails,
};

export default smartHomeApiClient;



// WEBPACK FOOTER //
// ./packages/smart-home-support/api/smartHomeApiClient.ts